import React from "react";

import { Add as AddIcon } from "@mui/icons-material";
import {
  Box,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Portal,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { MealPatternNameKind } from "@/gql/graphql";
import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";
import {
  mealPatternMacronutrients,
  mealPatternMultiplier,
  mealPatternNameLabel,
} from "@/utils/food";
import { percent, round } from "@/utils/number";

import AddMealPatternDialog from "./AddMealPatternDialog";
import InfoIcon from "../Icons/InfoIcon";
import MealPatternIcon from "../MealPlanForm/MealPlannerForm/MealPatternIcon";

interface Props {
  name: MealPatternNameKind;
  onAddMealPattern: (name: MealPatternNameKind, amountServings: number) => void;
}

const useStyles = makeStyles()((theme) => ({
  foodIcon: {
    color: colors.primary,
    backgroundColor: "rgba(31,212,175,0.1)", // #1FD4AF, 10% opacity
  },
  fruitIcon: {
    color: colors.secondary,
    backgroundColor: "rgba(255,157,55,0.1)", // #FF9D37, 10% opacity
  },
  infoIcon: {
    marginLeft: theme.spacing(2),
    fontSize: 16,
    color: "#C0C5CD",
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  textPrimary: {
    fontSize: 16,
    lineHeight: "22px",
    fontWeight: 600,
    color: colors.dark,
  },
  textSecondary: {
    display: "flex",
    alignItems: "center",
    fontSize: 14,
    lineHeight: "20px",
    fontWeight: 600,
    color: colors.grey,
  },
  tooltipArrow: {
    color: colors.dark,
  },
  tooltipTooltip: {
    backgroundColor: colors.dark,
    borderRadius: 0,
  },
  tooltipText: {
    fontSize: 12,
    lineHeight: "14px",
    fontWeight: 600,
    color: colors.white,
  },
  tooltipTextValue: {
    color: theme.palette.primary.main,
  },
  epWeight: {
    background: theme.palette.grey["200"],
    borderRadius: "4px",
    padding: "2px 8px",
    marginLeft: theme.spacing(2),
  },
}));

const MealPatternItem: React.FC<Props> = ({ name, onAddMealPattern }) => {
  const { classes } = useStyles();
  const portionDialog = useDialogState();

  const multiplier = mealPatternMultiplier(name);
  const macronutrients = mealPatternMacronutrients(name, 1);

  const macronutrientValue = (macro: "proteins" | "fats" | "carbohydrates") => {
    const { grams, percentage } = macronutrients[macro];
    return `${round(grams, 2)}g(${percent(percentage, { round: 0 })}%)`;
  };

  return (
    <ListItem
      disableGutters
      secondaryAction={
        <IconButton
          className={classes.addButton}
          aria-label="add"
          onClick={portionDialog.show}
          size="large"
        >
          <AddIcon />
        </IconButton>
      }
    >
      <ListItemAvatar>
        <MealPatternIcon name={name} />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box display="flex" justifyContent="space-between" marginRight={4}>
            <Typography variant="body1" color="textPrimary">
              {mealPatternNameLabel(name)}
            </Typography>
          </Box>
        }
        secondary={
          <>
            <span>{Math.round(multiplier.calories)} kcal</span>
            <Tooltip
              arrow
              classes={{
                arrow: classes.tooltipArrow,
                tooltip: classes.tooltipTooltip,
              }}
              title={
                <div>
                  <Typography className={classes.tooltipText}>
                    Proteins :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue("proteins")}
                    </span>
                    , Fats :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue("fats")}
                    </span>
                    ,<br /> Carbohydrates :{" "}
                    <span className={classes.tooltipTextValue}>
                      {macronutrientValue("carbohydrates")}
                    </span>
                  </Typography>
                </div>
              }
            >
              <InfoIcon className={classes.infoIcon} />
            </Tooltip>
          </>
        }
        classes={{
          primary: classes.textPrimary,
          secondary: classes.textSecondary,
        }}
      />
      <Portal>
        <AddMealPatternDialog
          {...portionDialog.props}
          name={name}
          onAddMealPattern={onAddMealPattern}
        />
      </Portal>
    </ListItem>
  );
};

export default MealPatternItem;
